import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';

import { UserContext } from './hooks/UserContext';
import Header from './components/Header'
import Loading from './components/Loading'
import AnimatedDots from './components/AnimatedDots'

export const ManageCategories = () => {
    const [courses, setCourses] = useState([])
    const [categories, setCategories] = useState([])
    const [selectedCategoryId, setSelectedCategoryId] = useState(null)
    const [first, setFirst] = useState(0);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [showManager, setShowManager] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('add'); // 'add' | 'edit'
    const [formId, setFormId] = useState(null);
    const [formName, setFormName] = useState('');
    const [formIconUrl, setFormIconUrl] = useState('');
    const [formComment, setFormComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    // Assign-to-category modal state
    const [showAssignDialog, setShowAssignDialog] = useState(false);
    const [assignFilter, setAssignFilter] = useState('');
    const [assignSelected, setAssignSelected] = useState(new Set());
    const [assigning, setAssigning] = useState(false);
    const [unassigning, setUnassigning] = useState(false);
    const { user } = useContext(UserContext);

    const getCourses = useCallback(async () => {
        const res = await axios.get(`/api/courses?per_page=500`, {
            headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms' }
        });
        return res.data.items;
    }, [user.token])

    const getCategories = useCallback(async () => {
        const res = await axios.get(`/api/courses/categories?per_page=200`, {
            headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms' }
        });
        return res.data.items;
    }, [user.token])

    const refreshCategories = useCallback(async () => {
        const list = await getCategories();
        const sorted = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(sorted);
        // keep current selection if still present; otherwise default to first
        if (selectedCategoryId && !sorted.find(c => c.id === selectedCategoryId)) {
            setSelectedCategoryId(sorted[0]?.id || null);
        }
    }, [getCategories, selectedCategoryId]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            const categories_data = await getCategories();
            const categories_sorted = [...categories_data].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setCategories(categories_sorted);
            if (categories_sorted?.length && !selectedCategoryId) {
                setSelectedCategoryId(categories_sorted[0].id);
            }

            const course_data = await getCourses();
            const data_modified = course_data.map(course => {
                const doNotMiss = !!course['do_not_miss'];
                // Extract category IDs from category objects
                const categoryIds = Array.isArray(course.categories)
                    ? course.categories.map(cat => typeof cat === 'object' ? cat.id : cat)
                    : [];
                return {
                    ...course,
                    category_ids: categoryIds,
                    last_edited_iso: course['last_edited'] ? convertDate(course['last_edited']) : '-',
                    published_date_iso: course['published_date'] ? convertDate(course['published_date']) : '-',
                    is_new: course['is_new'] ? 'new' : '-',
                    do_not_miss: doNotMiss ? 'on' : '',
                    do_not_miss_sort: doNotMiss ? 1 : 0,
                }
            });
            setCourses(data_modified);
            setLoading(false);
        }
        fetchData();
    }, [getCategories, getCourses, selectedCategoryId])

    // Close dialogs or panel with Esc
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (showDialog) {
                    setShowDialog(false);
                    return;
                }
                if (showAssignDialog) {
                    setShowAssignDialog(false);
                    return;
                }
                if (showManager) {
                    setShowManager(false);
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showDialog, showAssignDialog, showManager]);

    // Clear selected rows when category changes
    useEffect(() => {
        setSelectedCourses([]);
    }, [selectedCategoryId]);

    const convertDate = (timestamp) => {
        const offset = new Date(timestamp).getTimezoneOffset() * 60 * 1000;
        const dateTime = timestamp * 1000 - offset;
        const dateFormat = new Intl.DateTimeFormat("en-GB", {
            timeStyle: "medium",
            dateStyle: "medium",
        });
        return dateFormat.format(dateTime);
    }

    const getCategoryName = (catId) => categories.find(c => c.id === catId)?.name || null;

    const categoriesTemplate = (rowData) => {
        const cats = rowData.categories || [];
        const names = cats.map(cat => {
            if (typeof cat === 'object' && cat.name) return cat.name;
            if (typeof cat === 'number') return getCategoryName(cat);
            return null;
        }).filter(Boolean);
        return (
            <ul>
                {names.map((name, idx) => <li key={idx}>{name}</li>)}
            </ul>
        );
    }

    const nameBodyTemplate = (rowData) => {
        return <a href={`/editor?course=${rowData.id}`}>{rowData.name}</a>;
    }

    // Category Manager helpers
    const openAddDialog = () => {
        setDialogMode('add');
        setFormId(null);
        setFormName('');
        setFormIconUrl('');
        setFormComment('');
        setErrorMsg('');
        setShowDialog(true);
    }

    const openEditDialog = (cat) => {
        setDialogMode('edit');
        setFormId(cat.id);
        setFormName(cat.name || '');
        setFormIconUrl(cat.icon_url || '');
        setFormComment(cat.comment || '');
        setErrorMsg('');
        setShowDialog(true);
    }

    const saveCategory = async () => {
        if (!formName?.trim() || !formIconUrl?.trim()) {
            setErrorMsg('Name and Icon URL are required.');
            return;
        }
        setSaving(true);
        try {
            const payload = { name: formName.trim(), icon_url: formIconUrl.trim(), comment: formComment?.trim() || '' };
            let saved;
            if (dialogMode === 'add') {
                const res = await axios.post('/api/courses/categories', payload, {
                    headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms', 'Content-Type': 'application/json' }
                });
                saved = res.data;
            } else {
                const res = await axios.put(`/api/courses/categories/${formId}`, payload, {
                    headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms', 'Content-Type': 'application/json' }
                });
                saved = res.data;
            }
            await refreshCategories();
            if (saved?.id) setSelectedCategoryId(saved.id);
            // reset form
            setFormId(null); setFormName(''); setFormIconUrl(''); setFormComment('');
            setShowDialog(false);
        } catch (e) {
            setErrorMsg(e?.response?.data?.message || 'Failed to save category');
        } finally {
            setSaving(false);
        }
    }

    const deleteCategory = async (cat) => {
        if (!window.confirm(`Delete category "${cat.name}"? This will detach it from all topics.`)) return;
        try {
            await axios.delete(`/api/courses/categories/${cat.id}`, {
                headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms' }
            });
            await refreshCategories();
            // if the deleted category was selected, adjust selection happens in refreshCategories
        } catch (e) {
            alert(e?.response?.data?.message || 'Delete failed');
        }
    }

    // Assign-to-category helpers
    // Courses NOT in the selected category (for assignment modal)
    const unassignedCourses = selectedCategoryId
        ? courses.filter(c => Array.isArray(c.category_ids) && !c.category_ids.includes(selectedCategoryId))
        : [];

    const unassignedFiltered = unassignedCourses.filter(c => {
        if (!assignFilter) return true;
        const q = assignFilter.toLowerCase();
        return String(c.id).includes(q)
            || (c.name || '').toLowerCase().includes(q)
            || (c.type || '').toLowerCase().includes(q)
            || (c.status || '').toLowerCase().includes(q);
    });

    const toggleAssignSelection = (courseId) => {
        setAssignSelected(prev => {
            const next = new Set(prev);
            if (next.has(courseId)) next.delete(courseId); else next.add(courseId);
            return next;
        });
    };

    const selectAllAssign = () => setAssignSelected(new Set(unassignedFiltered.map(c => c.id)));
    const clearAssignSelection = () => setAssignSelected(new Set());

    const openAssignDialog = () => {
        if (!selectedCategoryId) return;
        setAssignFilter('');
        setAssignSelected(new Set());
        setShowAssignDialog(true);
    };

    const assignCoursesToCategory = async () => {
        if (!selectedCategoryId || assignSelected.size === 0) return;
        setAssigning(true);
        try {
            const ids = Array.from(assignSelected);
            await Promise.all(ids.map(async (courseId) => {
                const course = courses.find(c => c.id === courseId);
                if (!course) return null;
                const current = Array.isArray(course.category_ids) ? course.category_ids.slice() : [];
                const nextCategories = Array.from(new Set([...current, selectedCategoryId]));
                const payload = { categories: nextCategories };
                await axios.put(`/api/courses/${courseId}`, payload, {
                    headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms', 'Content-Type': 'application/json' }
                });
            }));
            // Refresh after assignment
            const course_data = await getCourses();
            const data_modified = course_data.map(course => {
                const doNotMiss = !!course['do_not_miss'];
                const categoryIds = Array.isArray(course.categories)
                    ? course.categories.map(cat => typeof cat === 'object' ? cat.id : cat)
                    : [];
                return {
                    ...course,
                    category_ids: categoryIds,
                    last_edited_iso: course['last_edited'] ? convertDate(course['last_edited']) : '-',
                    published_date_iso: course['published_date'] ? convertDate(course['published_date']) : '-',
                    is_new: course['is_new'] ? 'new' : '-',
                    do_not_miss: doNotMiss ? 'on' : '',
                    do_not_miss_sort: doNotMiss ? 1 : 0,
                }
            });
            setCourses(data_modified);
            await refreshCategories();
            setShowAssignDialog(false);
            setAssignSelected(new Set());
        } catch (e) {
            alert(e?.response?.data?.message || 'Failed to assign categories');
        } finally {
            setAssigning(false);
        }
    };

    const unassignSelectedFromCategory = async () => {
        if (!selectedCategoryId || selectedCourses.length === 0) return;
        if (!window.confirm(`Unassign ${selectedCourses.length} selected course(s) from the current category?`)) return;
        setUnassigning(true);
        try {
            const ids = selectedCourses.map(c => c.id);
            await Promise.all(ids.map(async (courseId) => {
                const course = courses.find(c => c.id === courseId);
                if (!course) return null;
                const current = Array.isArray(course.category_ids) ? course.category_ids.slice() : [];
                const nextCategories = current.filter(id => id !== selectedCategoryId);
                const payload = { categories: nextCategories };
                await axios.put(`/api/courses/${courseId}`, payload, {
                    headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms', 'Content-Type': 'application/json' }
                });
            }));
            // Refresh after unassignment
            const course_data = await getCourses();
            const data_modified = course_data.map(course => {
                const doNotMiss = !!course['do_not_miss'];
                const categoryIds = Array.isArray(course.categories)
                    ? course.categories.map(cat => typeof cat === 'object' ? cat.id : cat)
                    : [];
                return {
                    ...course,
                    category_ids: categoryIds,
                    last_edited_iso: course['last_edited'] ? convertDate(course['last_edited']) : '-',
                    published_date_iso: course['published_date'] ? convertDate(course['published_date']) : '-',
                    is_new: course['is_new'] ? 'new' : '-',
                    do_not_miss: doNotMiss ? 'on' : '',
                    do_not_miss_sort: doNotMiss ? 1 : 0,
                }
            });
            setCourses(data_modified);
            await refreshCategories();
            setSelectedCourses([]);
        } catch (e) {
            alert(e?.response?.data?.message || 'Failed to unassign categories');
        } finally {
            setUnassigning(false);
        }
    };

    const renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <label htmlFor="categorySelectHeader" className="mb-0" style={{ marginRight: 16 }}>Category</label>
                    <select
                        id="categorySelectHeader"
                        className="form-select"
                        style={{ minWidth: 220 }}
                        value={selectedCategoryId || ''}
                        onChange={(e) => setSelectedCategoryId(parseInt(e.target.value, 10))}
                    >
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="p-input-icon-left me-3">
                        <i className="pi pi-search" />
                        <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Global Search" />
                    </span>
                    <button type="button" className="btn btn-outline-primary" style={{ marginLeft: 24 }} disabled={!selectedCategoryId} onClick={openAssignDialog}>
                        Assign to Category
                    </button>
                    <button type="button" className="btn btn-outline-danger" style={{ marginLeft: 12 }}
                            disabled={!selectedCategoryId || selectedCourses.length === 0 || unassigning}
                            onClick={unassignSelectedFromCategory}>
                        {unassigning ? 'Unassigning…' : `Unassign${selectedCourses.length ? ` (${selectedCourses.length})` : ''}`}
                    </button>
                    <button type="button" className="btn btn-outline-primary" style={{ marginLeft: 12 }} onClick={() => setShowManager(v => !v)}>
                        {showManager ? 'Hide' : 'Manage Categories'}
                    </button>
                </div>
            </div>
        );
    }

    const filteredCourses = selectedCategoryId
        ? courses.filter(c => Array.isArray(c.category_ids) && c.category_ids.includes(selectedCategoryId))
        : [];

    return (
        <div className="App">
            <Header />
            <div className="container-fluid">
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <h2 className="mb-3">Manage Categories</h2>
                    </div>
                </div>
                <div className="row mt-1">
                    <div className="col-12 col-xl-10 mx-auto">
                        <div className="alert alert-info py-2 small mb-0" role="alert">
                            <strong>Note:</strong>
                            <ul className="mb-0">
                                <li>"Do Not Miss" checkbox: topic appears as a rotating hero image.</li>
                                <li>"Most Popular" category: shown in "Popular on Nerdish" — aim for 10–15 items (small vertical cards).</li>
                                <li>"Recommended for You" category: shown as "You May Like" cards with descriptions — add 4 items (3 on iPhone, 4 on iPad).</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {showManager && (
                    <div className="row mt-2">
                        <div className="col-12 col-xl-10 mx-auto">
                            <div className="card shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <h5 className="mb-0">Categories</h5>
                                            <button type="button" className="btn btn-primary" style={{ marginLeft: 24 }} onClick={openAddDialog}>Add Category</button>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                                            style={{ lineHeight: 1, width: 32, height: 32 }}
                                            aria-label="Close"
                                            title="Close (Esc)"
                                            onClick={() => setShowManager(false)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    
                                    <div className="table-responsive">
                                        <table className="table table-sm align-middle">
                                            <thead>
                                                <tr>
                            <th style={{ width: '24%' }}>Name</th>
                            <th style={{ width: '8%' }}>ID</th>
                                                    <th style={{width:'28%'}}>Icon URL</th>
                            <th style={{ width: '20%' }}>Comment</th>
                                                    <th style={{width:'10%'}} className="text-center">Topics</th>
                                                    <th style={{width:'10%'}} className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {categories.map(cat => (
                                                    <tr key={cat.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                {cat.icon_url ? (<img src={cat.icon_url} alt="icon" style={{width:20,height:20,objectFit:'cover'}} />) : null}
                                                                <span>{cat.name}</span>
                                                            </div>
                                                        </td>
                                                    <td>{cat.id}</td>
                                                        <td className="text-truncate" style={{maxWidth:260}}>
                                                            <a href={cat.icon_url} target="_blank" rel="noreferrer">{cat.icon_url}</a>
                                                        </td>
                                                        <td className="text-truncate" style={{maxWidth:220}}>{cat.comment}</td>
                                                        <td className="text-center">{cat.courses}</td>
                                                        <td className="text-end">
                                                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openEditDialog(cat)}>Edit</button>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => deleteCategory(cat)} disabled={cat.courses > 0}>Delete</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {categories.length === 0 && (
                            <tr><td colSpan="6" className="text-center text-muted py-3">No categories yet</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="row mt-4">
                    <div className="col-12">
                        <DataTable style={{ width: '90%', margin: '0 auto' }} responsiveLayout="scroll" header={renderHeader()} value={filteredCourses} paginator={!loading} rows={20} first={first} onPage={(e) => setFirst(e.first)}
                            selection={selectedCourses}
                            onSelectionChange={(e) => setSelectedCourses(e.value)}
                            dataKey="id"
                            globalFilter={globalFilter}
                            emptyMessage={
                                loading ? (
                                    <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                                        <Loading />
                                        <p style={{ marginTop: '1.5rem', color: '#6c757d', fontSize: '1.1rem', fontWeight: '500' }}>
                                            Loading categories<AnimatedDots />
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                                        {selectedCategoryId ? "No courses found for this category" : "Select a category to view courses"}
                                    </div>
                                )
                            }
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown">
                            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                            <Column field="name" header="Topic" body={nameBodyTemplate} sortable style={{ width: '20%' }}></Column>
                            <Column field="id" header="ID"></Column>
                            <Column field="topics" header="Sub-Topics"></Column>
                            <Column field="categories" header="Categories" body={categoriesTemplate} style={{ width: '20%' }}></Column>
                            <Column field="do_not_miss" sortField="do_not_miss_sort" header="Do Not Miss" sortable></Column>
                            <Column field="type" header="Type" sortable></Column>
                            <Column field="status" header="Status" sortable></Column>
                            <Column field="is_new" header="Is New?" sortable></Column>
                            <Column field="published_date_iso" header="Published" sortable></Column>
                            <Column field="last_edited_iso" header="Last Edited" sortable></Column>
                        </DataTable>
                    </div>
                </div>

                <Dialog header={dialogMode === 'add' ? 'Add Category' : 'Edit Category'} visible={showDialog} style={{ width: '500px' }} modal onHide={() => setShowDialog(false)}>
                    {errorMsg && <div className="alert alert-danger py-1 mb-3">{errorMsg}</div>}
                    <div className="mb-3">
                        <label className="form-label">Name</label>
                        <input className="form-control" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g., Art & Culture" />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Icon URL</label>
                        <input className="form-control" value={formIconUrl} onChange={e => setFormIconUrl(e.target.value)} placeholder="https://.../icon.png" />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Comment (optional)</label>
                        <textarea className="form-control" rows={2} value={formComment} onChange={e => setFormComment(e.target.value)} />
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDialog(false)} disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={saveCategory} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                    </div>
                </Dialog>
                <Dialog
                    header={selectedCategoryId ? `Assign to "${getCategoryName(selectedCategoryId) || 'Category'}"` : 'Assign to Category'}
                    style={{ width: '720px' }}
                    modal
                    baseZIndex={2000}
                    blockScroll
                    visible={showAssignDialog}
                    onHide={() => setShowAssignDialog(false)}
                >
                    <div className="mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="p-input-icon-left">
                                <i className="pi pi-search" />
                                <InputText type="search" value={assignFilter} onChange={(e) => setAssignFilter(e.target.value)} placeholder="Filter courses" />
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={selectAllAssign} disabled={unassignedFiltered.length === 0}>Select All</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearAssignSelection} disabled={assignSelected.size === 0}>Clear</button>
                        </div>
                    </div>
                    <div className="border rounded px-2" style={{ maxHeight: 360, overflowY: 'auto', overflowX: 'hidden' }}>
                        {unassignedFiltered.length === 0 ? (
                            <div className="text-center text-muted py-4">No courses available to assign.</div>
                        ) : (
                            <table className="table table-sm align-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{width:'40px'}} className="text-center"></th>
                                        <th style={{width:'10%'}}>ID</th>
                                        <th style={{width:'44%'}}>Name</th>
                                        <th style={{width:'20%'}}>Type</th>
                                        <th style={{width:'20%'}}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unassignedFiltered.map(c => (
                                        <tr key={c.id}>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    style={{ margin: 0, position: 'static' }}
                                                    checked={assignSelected.has(c.id)}
                                                    onChange={() => toggleAssignSelection(c.id)}
                                                />
                                            </td>
                                            <td>{c.id}</td>
                                            <td className="text-truncate" style={{maxWidth: 320}}>{c.name}</td>
                                            <td>{c.type}</td>
                                            <td>{c.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAssignDialog(false)} disabled={assigning}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={assignCoursesToCategory} disabled={assigning || assignSelected.size === 0}>
                            {assigning ? 'Assigning…' : `Assign ${assignSelected.size || ''}`}
                        </button>
                    </div>
                </Dialog>
            </div>
        </div>
    );
}
